import React, { useState } from "react";
import { Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";

const initial = {
  name: "",
  email: "",
  phone: "",
  car_model: "",
  budget_inr: "",
  notes: "",
};

export default function CustomRequestPage() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        budget_inr: form.budget_inr ? Number(form.budget_inr) : null,
        phone: form.phone || null,
        notes: form.notes || null,
      };
      await api.post("/custom-request", payload);
      toast.success("Request received. We'll be in touch.");
      setSuccess(true);
      setForm(initial);
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border border-white/15 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#FF2800] focus:outline-none transition-colors af-mono text-sm";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />
      <section className="border-b border-white/10 bg-black/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12 md:pt-28">
          <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2800] mb-4">
            // Custom workshop
          </p>
          <h1
            className="af-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.92] max-w-3xl"
            data-testid="custom-request-title"
          >
            Can't find your model?
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl">
            Tell us what you want built — vintage F1 legend, modern hypercar or
            the ride from your favourite game. Our workshop will engineer it
            brick-by-brick.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {success ? (
          <div
            className="af-card p-10 text-center"
            data-testid="custom-request-success"
          >
            <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
              // Pit lane
            </p>
            <h2 className="af-display text-3xl font-bold uppercase">
              Request received
            </h2>
            <p className="text-zinc-400 mt-3">
              We'll reach out within 48 hours with concept and quote.
            </p>
            <button
              type="button"
              onClick={() => setSuccess(false)}
              className="mt-8 border border-white/20 px-6 py-3 af-mono text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors"
              data-testid="custom-request-new"
            >
              Submit another
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="af-card p-8 md:p-12 space-y-6"
            data-testid="custom-request-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <label className="block">
                <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  Your name *
                </span>
                <input
                  type="text"
                  required
                  className={fieldClass}
                  value={form.name}
                  onChange={handle("name")}
                  data-testid="custom-name-input"
                />
              </label>
              <label className="block">
                <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  Email *
                </span>
                <input
                  type="email"
                  required
                  className={fieldClass}
                  value={form.email}
                  onChange={handle("email")}
                  data-testid="custom-email-input"
                />
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <label className="block">
                <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  Phone (optional)
                </span>
                <input
                  type="tel"
                  className={fieldClass}
                  value={form.phone}
                  onChange={handle("phone")}
                  data-testid="custom-phone-input"
                />
              </label>
              <label className="block">
                <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  Budget in ₹ (optional)
                </span>
                <input
                  type="number"
                  min="0"
                  className={fieldClass}
                  value={form.budget_inr}
                  onChange={handle("budget_inr")}
                  data-testid="custom-budget-input"
                />
              </label>
            </div>

            <label className="block">
              <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Car / model you want built *
              </span>
              <input
                type="text"
                required
                className={fieldClass}
                placeholder="e.g. 1992 Williams FW14B"
                value={form.car_model}
                onChange={handle("car_model")}
                data-testid="custom-model-input"
              />
            </label>

            <label className="block">
              <span className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Notes / references (optional)
              </span>
              <textarea
                rows={5}
                className={`${fieldClass} resize-none`}
                placeholder="Reference links, livery notes, scale preference…"
                value={form.notes}
                onChange={handle("notes")}
                data-testid="custom-notes-input"
              />
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 hover:bg-[#CC2000] disabled:opacity-50 transition-colors"
              data-testid="custom-submit-button"
            >
              {loading ? "Sending…" : "Send request"} <Send size={14} />
            </button>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
